import React from 'react';
import { theme } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { t } from 'i18next';
import { RoomConfigurationStepFormStyles } from './RoomConfigurationContent.styles';
import AssetsAssignmentSection from './FormComponents/AssetsAssignmentSection/AssetsAssignmentSection';
import SiteConfigurationSection from '@/comp/Forms/SiteSurveyForm/SiteConfigurationStep/SiteConfigurationSection';
import { TIER_OPTIONS } from '@/store/SiteSurveyForm/constants';
import { observer } from 'mobx-react-lite';
import { Form } from 'antd';
import { Room, RoomConfigurationContentProps } from './RoomConfigurationContent.utils';

const { useToken } = theme;

const RoomConfigurationContent = observer(
  ({
    siteSurveyStore,
    isCustomerInformationStepOpen,
    isSiteConfigurationStepOpen,
    toggleCustomerInformationStep,
    toggleSiteConfigurationStep,
    renderCustomerInformationStepContent,
    form,
    handleValidationChange,
    onCompressorDrawerVisibilityChange,
  }: RoomConfigurationContentProps) => {
    const { token } = useToken();
    const [rooms, setRooms] = React.useState<Room[]>([]);

    const getTierLabel = (value: string) => {
      if (!value) return '';
      const tierOption = TIER_OPTIONS.find(option => option.value === value);
      return tierOption ? t(tierOption.label) : '';
    };

    const pressureType =
      form.getFieldValue('pressureType') || siteSurveyStore.siteConfigurationData?.pressureType || 'main';
    const hasMultipleTargetPressures = Form.useWatch('hasMultipleTargetPressures', form) || false;
    const currentTier = form.getFieldValue('tier');

    const tierTag = currentTier ? <span className="tier-tag">{getTierLabel(currentTier)}</span> : null;

    return (
      <RoomConfigurationStepFormStyles theme={{ token }}>
        <div className="step-container">
          <div className="collapsible-section">
            {!isCustomerInformationStepOpen && (
              <div className="collapsed-customer-information-step-form">
                <div className="step-toggle-button" onClick={toggleCustomerInformationStep}>
                  <div className={`arrow-icon ${isCustomerInformationStepOpen ? 'open' : 'closed'}`}>
                    <DownOutlined />
                  </div>
                </div>
                <div className="form-header">{t('confirmCustomerDetails')}</div>
                <div className="customer-name">{siteSurveyStore.customerInformationData.customer?.name || ''}</div>
                <div className="account-details-row">
                  <div className="account-id-wrapper">
                    <span className="account-id-label">{t('Salesforce account ID')}</span>
                    <span className="account-id-value">
                      {siteSurveyStore.customerInformationData.customer?.accountId || ''}
                    </span>
                    {tierTag}
                  </div>
                </div>
              </div>
            )}

            {isCustomerInformationStepOpen && (
              <div className="expanded-customer-information-step-form">
                <div className="step-toggle-button" onClick={toggleCustomerInformationStep}>
                  <div className={`arrow-icon ${isCustomerInformationStepOpen ? 'open' : 'closed'}`}>
                    <DownOutlined />
                  </div>
                </div>
                <div className={`collapsible-content ${isCustomerInformationStepOpen ? 'open' : 'closed'}`}>
                  <div className="embedded-customer-information-step-form" style={{ width: '100%' }}>
                    {isCustomerInformationStepOpen && renderCustomerInformationStepContent()}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="collapsible-section">
            <div className="collapsed-site-configuration-step-form">
              <div className="step-toggle-button" onClick={toggleSiteConfigurationStep}>
                <div className={`arrow-icon ${isSiteConfigurationStepOpen ? 'open' : 'closed'}`}>
                  <DownOutlined />
                </div>
              </div>
              {!isSiteConfigurationStepOpen && <div className="form-header">{t('Define Site Configuration')}</div>}

              {isSiteConfigurationStepOpen && (
                <div className={`collapsible-content ${isSiteConfigurationStepOpen ? 'open' : 'closed'}`}>
                  <div className="embedded-site-configuration-step-form" style={{ width: '100%' }}>
                    {isSiteConfigurationStepOpen && <SiteConfigurationSection form={form} setRooms={setRooms} />}
                  </div>
                </div>
              )}
            </div>
          </div>

          <AssetsAssignmentSection
            form={form}
            rooms={rooms}
            setRooms={setRooms}
            hasMultipleTargetPressures={hasMultipleTargetPressures}
            onValidationChange={isValid => handleValidationChange(pressureType, isValid)}
            onDrawerVisibilityChange={onCompressorDrawerVisibilityChange}
          />
        </div>
      </RoomConfigurationStepFormStyles>
    );
  },
);

export default React.memo(RoomConfigurationContent);
